import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FileText, PlayCircle, MapPin, Video, Lock, Minus, Plus, Check, Star } from "lucide-react";
import { api, errMsg, fileUrl } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Stars } from "@/components/ProductCard";
import { Calendar } from "@/components/ui/calendar";
import { id as idLocale } from "date-fns/locale";
import { Checkbox } from "@/components/ui/checkbox";
import { startGoogleLogin } from "@/components/Auth";
import useSeo from "@/lib/useSeo";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const dayKey = (d) => new Date(d).toDateString();
const fmtDate = (s, lang) => new Date(s).toLocaleString(lang === "id" ? "id-ID" : "en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

function VariantPicker({ v, value, onChange }) {
  const { fmt } = useApp();
  return (
    <div className="space-y-2">
      <p className="text-sm">{v.name}: <span className="text-[#8C4F27]">{value}</span></p>
      <div className="flex flex-wrap gap-2">
        {v.options.map((o) => {
          const active = o.label === value;
          const id = `variant-${v.name}-${o.label}`.toLowerCase().replace(/\s+/g, "-");
          return o.color ? (
            <button key={o.label} onClick={() => onChange(o.label)} title={o.label} data-testid={id} aria-label={o.label}
              className={`w-9 h-9 rounded-full border-2 transition-transform hover:scale-110 ${active ? "border-[#3B220C] ring-2 ring-offset-2 ring-[#3B220C]" : "border-[#E6DFD5]"}`} style={{ background: o.color }} />
          ) : (
            <button key={o.label} onClick={() => onChange(o.label)} data-testid={id}
              className={`px-4 py-2 text-sm border transition-colors ${active ? "bg-[#3B220C] text-white border-[#3B220C]" : "border-[#E6DFD5] hover:border-[#3B220C]"}`}>
              {o.label}{o.delta ? ` +${fmt(o.delta)}` : ""}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SessionPicker({ p, value, onChange }) {
  const { t, lang } = useApp();
  const sessions = p.sessions.filter((s) => new Date(s.start) > new Date());
  const [day, setDay] = useState(sessions[0] ? new Date(sessions[0].start) : new Date());
  const open = sessions.filter((s) => s.capacity > s.booked).map((s) => new Date(s.start));
  const full = sessions.filter((s) => s.capacity <= s.booked).map((s) => new Date(s.start));
  const sameDay = sessions.filter((s) => dayKey(s.start) === dayKey(day));
  return (
    <div className="space-y-3" data-testid="session-picker">
      <p className="text-sm">{t("sessions")}</p>
      <div className="flex flex-col sm:flex-row gap-4 border border-[#E6DFD5] bg-white p-3">
        <Calendar mode="single" locale={lang === "id" ? idLocale : undefined} selected={day} onSelect={(d) => d && setDay(d)} defaultMonth={day}
          modifiers={{ open, full }} modifiersClassNames={{ open: "!bg-[#2E6B40] !text-white font-semibold rounded-full", full: "line-through !bg-[#E6DFD5] !text-[#9E2A2B] rounded-full" }} />
        <div className="flex-1 space-y-2 pt-2">
          {sameDay.length === 0 && <p className="text-sm text-[#6E6259]">No session on this day.</p>}
          {sameDay.map((s) => {
            const left = s.capacity - s.booked;
            return (
              <button key={s.session_id} disabled={left <= 0} onClick={() => onChange(s)} data-testid={`session-option-${s.session_id}`}
                className={`w-full text-left border px-4 py-3 text-sm transition-colors disabled:opacity-40 ${value?.session_id === s.session_id ? "border-[#3B220C] bg-[#3B220C] text-white" : "border-[#E6DFD5] hover:border-[#3B220C]"}`}>
                <span className="block">{fmtDate(s.start, lang)}</span>
                <span className="text-xs opacity-70">{left > 0 ? `${left}/${s.capacity} ${t("seats_left")}` : t("full")}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex flex-wrap gap-4 text-xs text-[#6E6259]" data-testid="session-legend">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-[#2E6B40]" />{t("open_dates")}</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-[#E6DFD5] border border-[#9E2A2B]" />{t("full_dates")}</span>
      </div>
      <div className="flex flex-wrap gap-1 text-xs text-[#6E6259]">
        {sessions.map((s) => <span key={s.session_id} className="border border-[#E6DFD5] px-2 py-1">{new Date(s.start).toLocaleDateString(lang === "id" ? "id-ID" : "en-GB")} · {s.capacity - s.booked}/{s.capacity}</span>)}
      </div>
    </div>
  );
}

function StarPicker({ value, onChange }) {
  return (
    <div className="flex gap-1" data-testid="review-star-picker">
      {[1, 2, 3, 4, 5].map((i) => (
        <button key={i} type="button" onClick={() => onChange(i)} data-testid={`review-star-${i}`} aria-label={`${i}`}>
          <Star size={26} strokeWidth={1.4} className={i <= value ? "fill-[#C47A37] text-[#C47A37]" : "text-[#C9BBAA]"} />
        </button>
      ))}
    </div>
  );
}

function ReviewForm({ p, onAdded }) {
  const { t, user } = useApp();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [anon, setAnon] = useState(false);
  const [can, setCan] = useState(null);
  const [ask, setAsk] = useState(false);
  useEffect(() => {
    if (user) { api.get(`/products/${p.product_id}/can-review`).then((r) => setCan(r.data)).catch(() => {}); }
  }, [user, p.product_id]);
  const submit = async () => {
    setAsk(false);
    try { await api.post(`/products/${p.product_id}/reviews`, { rating, comment, anonymous: anon }); setComment(""); setCan({ can_review: false, already: true }); toast.success(t("rate_thanks")); onAdded(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (!user) return <button onClick={() => { localStorage.setItem("ce_return", window.location.pathname); window.location.assign("/login"); }} className="mt-6 text-sm underline text-left" data-testid="review-login-btn">{t("rate_need_login")}</button>;
  if (!can) return null;
  if (!can.can_review) return <p className="mt-6 text-sm text-[#6E6259]" data-testid="review-locked">{can.already ? t("rate_done") : t("rate_need_buy")}</p>;
  return (
    <div className="mt-8 space-y-3" data-testid="review-form">
      <p className="text-sm">{t("write_review")}</p>
      <StarPicker value={rating} onChange={setRating} />
      <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} placeholder={`${t("rate_comment")} (${t("optional")})`} data-testid="review-comment-input" className="w-full border border-[#E6DFD5] p-3 bg-white" />
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={anon} onChange={(e) => setAnon(e.target.checked)} data-testid="review-anonymous-checkbox" />{t("rate_anon")}</label>
      <button disabled={!rating} onClick={() => setAsk(true)} data-testid="review-submit-btn" className="bg-[#3B220C] text-white px-6 py-2 text-sm disabled:opacity-40">{t("submit")}</button>
      {!rating && <p className="text-xs text-[#6E6259]">{t("rate_pick")}</p>}
      <AlertDialog open={ask} onOpenChange={setAsk}>
        <AlertDialogContent className="rounded-none" data-testid="review-confirm-dialog">
          <AlertDialogHeader><AlertDialogTitle className="font-serif text-2xl font-normal">{t("rate_confirm").replace("{n}", rating)}</AlertDialogTitle></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="review-confirm-cancel">{t("rate_cancel")}</AlertDialogCancel>
            <AlertDialogAction className="rounded-none bg-[#3B220C]" onClick={submit} data-testid="review-confirm-yes">{t("rate_yes")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Reviews({ p, onAdded }) {
  const { t } = useApp();
  return (
    <section className="mt-20 grid lg:grid-cols-3 gap-10" data-testid="reviews-block">
      <div>
        <h2 className="font-serif text-3xl">{t("reviews")}</h2>
        <div className="flex items-center gap-3 mt-3"><Stars value={p.rating} size={18} /><span className="text-sm text-[#6E6259]" data-testid="product-rating-summary">{p.rating} · {p.review_count}</span></div>
        <ReviewForm p={p} onAdded={onAdded} />
      </div>
      <div className="lg:col-span-2 divide-y divide-[#E6DFD5] max-h-[960px] overflow-y-auto pr-2" data-testid="reviews-list">
        {p.reviews.length === 0 && <p className="text-[#6E6259]">—</p>}
        {p.reviews.map((r) => (
          <div key={r.review_id} className="py-5">
            <div className="flex items-center gap-3"><Stars value={r.rating} /><span className="text-sm font-medium">{r.user_name}</span>{r.verified && <span className="text-[10px] font-mono text-[#2E6B40]">VERIFIED</span>}</div>
            {r.comment && <p className="mt-2 text-[#4a3f38]">{r.comment}</p>}
          </div>
        ))}
      </div>
    </section>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { t, fmt, pick, cart, user, lang } = useApp();
  const [p, setP] = useState(null);
  const [img, setImg] = useState(0);
  const [variant, setVariant] = useState({});
  const [qty, setQty] = useState(1);
  const [session, setSession] = useState(null);
  const [withKit, setWithKit] = useState(false);
  const [optImg, setOptImg] = useState(null);

  const load = () => api.get(`/products/${slug}`).then((r) => {
    setP(r.data);
    setVariant({ ...Object.fromEntries(r.data.variants.map((v) => [v.name, v.options[0].label])),
      ...(r.data.type === "digital" && r.data.files?.length ? { Format: r.data.files[0].format } : {}) });
  }).catch(() => setP(false));
  useEffect(() => { load(); setImg(0); setOptImg(null); setSession(null); setWithKit(false); }, [slug]); // eslint-disable-line

  const price = useMemo(() => {
    if (!p) return 0;
    if (p.type === "digital") return p.price + (p.files.find((f) => f.format === variant.Format)?.delta || 0);
    return p.price + p.variants.reduce((s, v) => s + (v.options.find((o) => o.label === variant[v.name])?.delta || 0), 0);
  }, [p, variant]);

  useSeo({
    title: p ? p.name : "", description: p ? p.description : "", image: p ? fileUrl(p.images?.[0]) : "",
    jsonLd: p ? { "@type": "Product", name: p.name, image: p.images, description: p.description,
      offers: { "@type": "Offer", priceCurrency: "IDR", price: p.price, availability: p.type !== "physical" || p.stock > 0 ? "InStock" : "OutOfStock" },
      ...(p.review_count ? { aggregateRating: { "@type": "AggregateRating", ratingValue: p.rating, reviewCount: p.review_count } } : {}) } : null,
  });

  if (p === false) return <p className="text-center py-32" data-testid="product-not-found">Product not found.</p>;
  if (!p) return <div className="h-[60vh]" />;

  const needsLogin = p.type !== "physical" && !user;
  const outOfStock = p.type === "physical" && p.stock <= 0;
  const preorder = outOfStock && p.out_of_stock_mode === "preorder";
  const soldOut = outOfStock && !preorder;
  const addToCart = (goToCart) => {
    if (needsLogin) { startGoogleLogin(`/product/${p.slug}`); return; }
    if (p.type === "workshop" && !session) { toast.error(t("sessions")); return; }
    cart.add({ product_id: p.product_id, slug: p.slug, name: p.name, type: p.type, image: p.images?.[0], price, qty, weight_g: p.weight_g || 0, variant: p.variants.length || (p.type === "digital" && p.files.length) ? variant : {},
      session_id: session?.session_id, session_start: session?.start });
    if (withKit && p.addon) cart.add({ product_id: p.addon.product_id, slug: p.addon.slug, name: p.addon.name, type: "physical", image: p.addon.images?.[0], price: p.addon.price, qty: 1, weight_g: p.addon.weight_g || 0, variant: {} });
    toast.success(t("added"));
    if (goToCart) nav("/cart");
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-12" data-testid="product-detail-page">
      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7 space-y-3">
          <div className="lg:h-[calc(100vh-180px)] max-h-[860px] bg-[#F6F2EE] overflow-hidden flex items-center justify-center" data-testid="product-image-frame"><img src={fileUrl(optImg || p.images?.[img])} alt={p.name} className="w-full h-auto max-h-[75vh] lg:w-auto lg:max-w-full lg:max-h-full object-contain" data-testid="product-main-image" /></div>
          <div className="flex gap-3 overflow-x-auto">
            {p.images.map((s, i) => <button key={s} onClick={() => { setImg(i); setOptImg(null); }} data-testid={`product-thumb-${i}`} className={`w-20 h-20 shrink-0 overflow-hidden border-2 ${i === img ? "border-[#3B220C]" : "border-transparent"}`}><img src={fileUrl(s)} alt="" className="w-full h-full object-cover" /></button>)}
          </div>
        </div>
        <div className="lg:col-span-5 space-y-7 lg:sticky lg:top-32 self-start">
          <div className="space-y-3">
            <p className="font-mono text-xs tracking-[0.2em] text-[#8C4F27]">{t(p.type).toUpperCase()}</p>
            <h1 className="font-serif text-4xl sm:text-5xl leading-[1.05]" data-testid="product-name">{pick(p, "name")}</h1>
            {p.review_count > 0 && <div className="flex items-center gap-2 text-sm text-[#6E6259]"><Stars value={p.rating} />({p.review_count})</div>}
            <p className="text-2xl flex flex-wrap items-baseline gap-3" data-testid="product-detail-price">{fmt(price)}{p.original_price && <><s className="text-base text-[#6E6259]" data-testid="product-detail-original-price">{fmt(p.original_price + (price - p.price))}</s><span className="bg-[#9E2A2B] text-white text-xs px-2 py-1" data-testid="product-detail-discount-badge">{p.discount_label}</span></>}</p>
          </div>
          <p className="text-[#6E6259] leading-relaxed">{pick(p, "description")}</p>

          {p.variants.map((v) => <VariantPicker key={v.name} v={v} value={variant[v.name]} onChange={(val) => { setVariant({ ...variant, [v.name]: val }); const o = v.options.find((x) => x.label === val); if (o?.image) setOptImg(o.image); }} />)}
          {p.type === "physical" && (
            <div className="text-xs text-[#6E6259] space-y-1" data-testid="fulfilment-notes">
              {preorder && <p className="text-[#8C4F27] font-medium" data-testid="preorder-note">{t("po_ships").replace("{n}", p.preorder_days || 14)}</p>}
              <p>{t("prep").replace("{n}", p.prep_days ?? 2)}</p>
            </div>
          )}

          {p.type === "digital" && (
            <div className="space-y-4">
              {p.files.length > 0 && <VariantPicker v={{ name: t("choose_format"), options: p.files.map((f) => ({ label: f.format, delta: f.delta })) }} value={variant.Format} onChange={(val) => setVariant({ ...variant, Format: val })} />}
              <div className="border border-[#E6DFD5] bg-white p-5 space-y-2 text-sm" data-testid="digital-info">
                <p className="flex items-center gap-2"><FileText size={16} />{t("formats")}: {variant.Format || "—"}</p>
                <p className="text-[#6E6259]">{t("download_limit")}: {p.download_limit}x</p>
              </div>
            </div>
          )}
          {p.type === "course" && (
            <div className="border border-[#E6DFD5] bg-white divide-y divide-[#E6DFD5]" data-testid="course-lessons">
              <p className="px-5 py-3 font-mono text-xs tracking-[0.2em] text-[#8C4F27]">{t("lessons").toUpperCase()} · {p.lessons.length}</p>
              {p.lessons.map((l, i) => <p key={l.lesson_id} className="px-5 py-3 text-sm flex items-center gap-3"><PlayCircle size={16} className="text-[#8C4F27]" /><span className="flex-1">{i + 1}. {l.title}</span><span className="text-[#6E6259] text-xs">{l.duration}</span></p>)}
            </div>
          )}
          {p.type === "workshop" && (
            <>
              <p className="flex items-center gap-2 text-sm text-[#6E6259]">{p.mode === "zoom" ? <><Video size={16} />Zoom · link in your account after booking</> : <><MapPin size={16} />{p.venue}</>}</p>
              <SessionPicker p={p} value={session} onChange={setSession} />
            </>
          )}
          {p.addon && ["course", "workshop"].includes(p.type) && (
            <label className="flex items-center gap-4 border border-dashed border-[#8C4F27] p-4 cursor-pointer bg-[#F6F2EE]" data-testid="addon-kit-upsell">
              <Checkbox checked={withKit} onCheckedChange={setWithKit} data-testid="addon-kit-checkbox" />
              <img src={fileUrl(p.addon.images?.[0])} alt="" className="w-14 h-14 object-cover" />
              <span className="flex-1 text-sm">{t("addon")}<span className="block text-[#6E6259]">{pick(p.addon, "name")} · +{fmt(p.addon.price)}</span></span>
            </label>
          )}

          {["physical", "workshop"].includes(p.type) && (
            <div className="flex items-center gap-4">
              <span className="text-sm">{t("qty")}</span>
              <div className="flex items-center border border-[#E6DFD5]">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-3" data-testid="qty-decrease"><Minus size={14} /></button>
                <span className="w-8 text-center" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty(Math.min(p.type === "physical" && !preorder ? p.stock : 10, qty + 1))} className="p-3" data-testid="qty-increase"><Plus size={14} /></button>
              </div>
              {p.type === "physical" && !preorder && <span className="text-xs text-[#6E6259]" data-testid="stock-indicator">{p.stock} {t("in_stock")}</span>}
            </div>
          )}

          <div className="flex gap-3">
            <button disabled={soldOut} onClick={() => addToCart(false)} data-testid="add-to-cart-btn"
              className="flex-1 bg-[#3B220C] text-white py-4 text-sm tracking-wider hover:bg-[#4A2C11] transition-colors disabled:opacity-40 flex items-center justify-center gap-2">
              {needsLogin && <Lock size={14} />}{soldOut ? t("out_of_stock") : needsLogin ? t("login_required") : preorder ? t("preorder") : t("add_to_cart")}
            </button>
            {!soldOut && !needsLogin && <button onClick={() => addToCart(true)} data-testid="buy-now-btn" className="px-6 border border-[#3B220C] text-sm hover:bg-[#F6F2EE] transition-colors"><Check size={16} /></button>}
          </div>
          {lang === "id" && p.type === "physical" && <p className="text-xs text-[#6E6259]">Bisa Ambil di Toko atau dikirim JNE / J&T / SiCepat / DHL / FedEx.</p>}
        </div>
      </div>
      <Reviews p={p} onAdded={load} />
    </div>
  );
}
